import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Clock, Shield, Users } from "lucide-react";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Footer from "../../components/Footer/Footer";
import "./ResourceOptimizer.css";

/* Floating orb background */
function BookingOrbs() {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute",
        width: "clamp(400px, 50vw, 900px)", height: "clamp(400px, 50vw, 900px)",
        background: "radial-gradient(circle, rgba(40,180,174,0.12) 0%, transparent 70%)",
        top: "-20%", left: "-10%",
        filter: "blur(80px)",
        animation: "ro-orb-drift-1 25s ease-in-out infinite",
      }} />
      <div style={{
        position: "absolute",
        width: "clamp(300px, 35vw, 600px)", height: "clamp(300px, 35vw, 600px)",
        background: "radial-gradient(circle, rgba(99,102,241,0.10) 0%, transparent 70%)",
        bottom: "-10%", right: "-5%",
        filter: "blur(80px)",
        animation: "ro-orb-drift-2 30s ease-in-out infinite",
      }} />
    </div>
  );
}

/* Trust badge */
function TrustBadge({ icon: Icon, text }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "8px",
      padding: "8px 14px",
      borderRadius: "100px",
      border: "1px solid var(--ro-border-default)",
      background: "var(--ro-bg-elevated)",
      fontSize: "12px",
      fontFamily: "'Inter', sans-serif",
      color: "var(--ro-text-muted)",
      fontWeight: 500,
    }}>
      <Icon size={13} strokeWidth={2} style={{ color: "var(--ro-accent)" }} />
      {text}
    </div>
  );
}

/* Cal.com inline embed */
function CalEmbed() {
  const [loaded, setLoaded] = useState(false);

  return (
    <div style={{ position: "relative", minHeight: "700px" }}>
      {!loaded && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 1,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: "16px",
          background: "var(--ro-bg-surface)",
        }}>
          <div style={{
            width: "32px", height: "32px",
            border: "2px solid var(--ro-border-default)",
            borderTop: "2px solid var(--ro-accent)",
            borderRadius: "50%",
            animation: "ro-spin 0.8s linear infinite",
          }} />
          <span style={{
            fontSize: "13px", fontFamily: "'Inter', sans-serif",
            color: "var(--ro-text-muted)",
          }}>
            Loading calendar...
          </span>
        </div>
      )}
      <iframe
        src="https://cal.com/teamgrid-sales-bd6ovp/30min?embed=true&embedType=inline"
        width="100%"
        height="700"
        frameBorder="0"
        onLoad={() => setLoaded(true)}
        style={{
          display: "block",
          border: "none",
          minHeight: "700px",
        }}
        title="Book a 30-minute audit"
        allow="payment"
      />
    </div>
  );
}

const ResourceOptimizerBookPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Book Free Audit - Resource Optimizer | TeamGrid</title>
        <meta
          name="description"
          content="Book a free 30-minute audit. We'll show you 3-5 areas where you're bleeding time and money."
        />
        <link rel="canonical" href="https://teamgrid.ai/resource-optimizer/book" />
      </Helmet>

      <div className="resource-optimizer-page">
        <PublicHeader />

        <div style={{ minHeight: "100vh", position: "relative", paddingTop: "120px" }}>
          <BookingOrbs />

          {/* Main content */}
          <main style={{
            position: "relative", zIndex: 1,
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "0 32px 80px",
          }}>
            {/* Header */}
            <div style={{ textAlign: "center", paddingBottom: "48px" }}>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                padding: "6px 14px",
                borderRadius: "100px",
                border: "1px solid rgba(40,180,174,0.25)",
                background: "rgba(40,180,174,0.06)",
                marginBottom: "20px",
              }}>
                <div style={{
                  width: "6px", height: "6px", borderRadius: "50%",
                  background: "var(--ro-green)",
                  boxShadow: "0 0 8px rgba(34,197,94,0.6)",
                  animation: "ro-pulse-glow 2s ease infinite",
                }} />
                <span style={{
                  fontSize: "11px", fontFamily: "'Inter', sans-serif", fontWeight: 600,
                  color: "var(--ro-accent)", textTransform: "uppercase", letterSpacing: "0.1em",
                }}>
                  Free 30-Minute Audit
                </span>
              </div>

              <h1 style={{
                fontFamily: "'Inter', sans-serif", fontWeight: 800,
                fontSize: "clamp(32px, 4vw, 56px)", lineHeight: 1.1,
                color: "var(--ro-text-primary)", letterSpacing: "-0.03em",
                marginBottom: "16px",
              }}>
                Book your free audit
              </h1>

              <p style={{
                fontFamily: "'Inter', sans-serif", fontSize: "16px",
                lineHeight: 1.7, color: "var(--ro-text-secondary)",
                maxWidth: "480px", margin: "0 auto 28px",
              }}>
                We'll show you 3-5 areas where you're bleeding time and money.
                No pitch deck. No sales script. Just numbers.
              </p>

              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: "10px", flexWrap: "wrap",
              }}>
                <TrustBadge icon={Clock} text="30 minutes" />
                <TrustBadge icon={Shield} text="No commitment" />
                <TrustBadge icon={Users} text="14 orgs transformed" />
              </div>
            </div>

            {/* Two-column layout */}
            <div className="ro-book-grid" style={{
              display: "grid",
              gridTemplateColumns: "320px 1fr",
              gap: "32px",
              alignItems: "start",
            }}>
              {/* Left panel */}
              <div style={{
                display: "flex", flexDirection: "column", gap: "20px",
                position: "sticky", top: "32px",
              }}>
                {/* What happens card */}
                <div style={{
                  padding: "28px",
                  borderRadius: "16px",
                  border: "1px solid var(--ro-border-default)",
                  background: "var(--ro-bg-surface)",
                }}>
                  <h3 style={{
                    fontFamily: "'Inter', sans-serif", fontWeight: 700,
                    fontSize: "14px", color: "var(--ro-text-primary)",
                    textTransform: "uppercase", letterSpacing: "0.08em",
                    marginBottom: "20px",
                  }}>
                    What happens in 30 min
                  </h3>

                  {[
                    { num: "01", title: "We map your team", desc: "Quick walkthrough of your current workflows and where time is actually going." },
                    { num: "02", title: "We identify the leaks", desc: "3-5 specific areas with quantified time and cost waste." },
                    { num: "03", title: "We show the fix", desc: "Exactly which AI systems would eliminate each problem - no vague promises." },
                  ].map((step) => (
                    <div key={step.num} style={{
                      display: "flex", gap: "14px",
                      paddingBottom: "18px",
                      marginBottom: "18px",
                      borderBottom: step.num !== "03" ? "1px solid var(--ro-border-subtle)" : "none",
                    }}>
                      <span style={{
                        fontFamily: "'Inter', sans-serif", fontWeight: 700,
                        fontSize: "13px", color: "var(--ro-accent)", opacity: 0.6,
                        flexShrink: 0, paddingTop: "2px",
                      }}>
                        {step.num}
                      </span>
                      <div>
                        <div style={{
                          fontFamily: "'Inter', sans-serif", fontWeight: 600,
                          fontSize: "14px", color: "var(--ro-text-primary)",
                          marginBottom: "4px",
                        }}>
                          {step.title}
                        </div>
                        <div style={{
                          fontFamily: "'Inter', sans-serif", fontSize: "12px",
                          color: "var(--ro-text-muted)", lineHeight: 1.6,
                        }}>
                          {step.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Social proof card */}
                <div style={{
                  padding: "24px",
                  borderRadius: "16px",
                  border: "1px solid var(--ro-border-default)",
                  background: "var(--ro-bg-surface)",
                }}>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                    {[1, 2, 3, 4, 5].map(i => (
                      <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="var(--ro-accent)" style={{ opacity: 0.9 }}>
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    ))}
                  </div>
                  <p style={{
                    fontFamily: "'Inter', sans-serif", fontSize: "13px",
                    color: "var(--ro-text-secondary)", lineHeight: 1.7,
                    marginBottom: "14px", fontStyle: "italic",
                  }}>
                    "We found ₹18L/month in recoverable waste in the first session. Worth every minute."
                  </p>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{
                      width: "32px", height: "32px", borderRadius: "50%",
                      background: "var(--ro-accent-dim)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "'Inter', sans-serif", fontWeight: 700,
                      fontSize: "13px", color: "var(--ro-accent)",
                    }}>
                      R
                    </div>
                    <div>
                      <div style={{
                        fontFamily: "'Inter', sans-serif", fontWeight: 600,
                        fontSize: "12px", color: "var(--ro-text-primary)",
                      }}>
                        Rajesh M.
                      </div>
                      <div style={{
                        fontFamily: "'Inter', sans-serif", fontSize: "11px",
                        color: "var(--ro-text-muted)",
                      }}>
                        COO, Logistics Co.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - Cal.com embed */}
              <div style={{
                borderRadius: "20px",
                border: "1px solid var(--ro-border-default)",
                background: "var(--ro-bg-surface)",
                overflow: "hidden",
                boxShadow: "0 8px 40px rgba(0,0,0,0.3), 0 0 80px rgba(40,180,174,0.04)",
              }}>
                <div style={{
                  padding: "20px 24px",
                  borderBottom: "1px solid var(--ro-border-subtle)",
                  display: "flex", alignItems: "center", gap: "10px",
                }}>
                  <div style={{
                    width: "8px", height: "8px", borderRadius: "50%",
                    background: "var(--ro-green)",
                    boxShadow: "0 0 8px rgba(34,197,94,0.5)",
                    animation: "ro-pulse-glow 2s ease infinite",
                  }} />
                  <span style={{
                    fontFamily: "'Inter', sans-serif", fontWeight: 500,
                    fontSize: "13px", color: "var(--ro-text-secondary)",
                  }}>
                    Pick a time that works for you
                  </span>
                </div>
                <div style={{ padding: "0" }}>
                  <CalEmbed />
                </div>
              </div>
            </div>
          </main>
        </div>

        <Footer />
      </div>

      <style>{`
        @keyframes ro-spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 900px) {
          .ro-book-grid {
            grid-template-columns: 1fr !important;
          }
          .ro-book-grid > div:first-child {
            position: static !important;
          }
        }
        @media (max-width: 640px) {
          .resource-optimizer-page nav {
            padding: 16px 20px !important;
          }
        }
      `}</style>
    </>
  );
};

export default ResourceOptimizerBookPage;
